import React, { useMemo, useState } from 'react'
import Drawer from '@mui/material/Drawer'
import { Comment, SavedAudience } from '@marketing-milk/interfaces'
import { formatDistance } from 'date-fns'
import {
  SECTIONS,
  SectionKey,
  sectionNumberFor,
} from 'pages/business/sprint-packets/editor/sections'
import { radius, SurfacePalette, type } from 'pages/business/sprint-packets/tokens'
import { CommentAnchor } from './CommentThread'
import { AnchorGroup, groupCommentsByAnchor } from './groupCommentsByAnchor'

/**
 * Per-packet "All comments" navigator (v1.4b). A read-only, left-anchored drawer
 * listing every packet comment grouped by anchor, with status + location filters.
 * Selecting a comment fires `onSelectComment(anchor, commentId)`; the host closes
 * this drawer, navigates to the anchor (scroll in Edit, slide jump in Present), and
 * opens the right-side `CommentThread` on that comment. Pure view over the already
 * hydrated `comments` — no fetch; all grouping/ordering lives in
 * {@link groupCommentsByAnchor}.
 */

type StatusFilter = 'unresolved' | 'all' | 'resolved'
type LocationFilter = 'all' | 'entities' | SectionKey

interface Props {
  open: boolean
  onClose: () => void
  /** light (Edit) / dark (Present) — the drawer renders over either surface. */
  palette: SurfacePalette
  comments: Comment[]
  /** For the audience-label fallback (Present mounts a single slide). */
  audiences: SavedAudience[]
  onSelectComment: (anchor: CommentAnchor, commentId: number) => void
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'all', label: 'All' },
  { value: 'resolved', label: 'Resolved' },
]

const formatRelative = (iso: string) =>
  formatDistance(new Date(iso), new Date(), { addSuffix: true })

const authorName = (c: Comment): string =>
  c.author ? `${c.author.firstName} ${c.author.lastName}` : 'Deleted user'

const matchesStatus = (c: Comment, status: StatusFilter): boolean =>
  status === 'all' ? true : status === 'resolved' ? c.resolved : !c.resolved

const matchesLocation = (group: AnchorGroup, location: LocationFilter): boolean =>
  location === 'all'
    ? true
    : location === 'entities'
    ? group.isEntity
    : group.sectionKey === location

export const AllCommentsDrawer = ({
  open,
  onClose,
  palette,
  comments,
  audiences,
  onSelectComment,
}: Props) => {
  const [status, setStatus] = useState<StatusFilter>('unresolved')
  const [location, setLocation] = useState<LocationFilter>('all')

  const groups = useMemo(() => groupCommentsByAnchor(comments), [comments])

  // Fallback-first label resolver. Section-family anchors resolve from SECTIONS;
  // entity anchors prefer a live `data-comment-label` off a mounted card (an
  // optional enhancement — Edit mounts every card) but always have a typed
  // fallback so they stand alone in Present, where one slide is mounted.
  const resolveLabel = (group: AnchorGroup): { number?: string; label: string } => {
    if (!group.isEntity) {
      const section = SECTIONS.find(s => s.key === group.sectionKey)
      if (section) return { number: sectionNumberFor(section.key), label: section.label }
      return { label: group.anchorID }
    }
    const live = liveEntityLabel(group.anchorType, group.anchorID)
    if (live) return { label: live }
    if (group.anchorType === 'audience') {
      const a = audiences.find(x => String(x.id) === group.anchorID)
      return { label: a?.name ?? `Audience #${group.anchorID}` }
    }
    return { label: `Creative #${group.anchorID}` }
  }

  const visibleGroups = groups
    .filter(g => matchesLocation(g, location))
    .map(g => ({ group: g, rows: g.rootComments.filter(c => matchesStatus(c, status)) }))
    .filter(({ rows }) => rows.length > 0)

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <div
        style={{
          width: 380,
          maxWidth: '90vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: palette.surface,
          color: palette.text,
          fontFamily: type.family.sans,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 20px 12px',
          }}
        >
          <span
            style={{
              fontSize: type.size.h3,
              fontWeight: type.weight.semibold,
              color: palette.text,
            }}
          >
            All comments
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close all comments"
            style={{
              border: 'none',
              background: 'transparent',
              color: palette.textMuted,
              cursor: 'pointer',
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '0 20px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 6 }} role="group" aria-label="Filter by status">
            {STATUS_OPTIONS.map(opt => {
              const active = status === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  aria-pressed={active}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: radius.sm,
                    border: `1px solid ${active ? palette.accent : palette.hairline}`,
                    background: active ? palette.accent : 'transparent',
                    color: active ? palette.accentInk : palette.textMuted,
                    fontFamily: type.family.sans,
                    fontSize: type.size.eyebrow,
                    fontWeight: type.weight.medium,
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>

          <select
            aria-label="Filter by location"
            value={location}
            onChange={e => setLocation(e.target.value as LocationFilter)}
            style={{
              width: '100%',
              padding: '7px 10px',
              borderRadius: radius.sm,
              border: `1px solid ${palette.hairline}`,
              background: palette.bg,
              color: palette.text,
              fontFamily: type.family.sans,
              fontSize: type.size.body,
              cursor: 'pointer',
            }}
          >
            <option value="all">All locations</option>
            {SECTIONS.map(s => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
            <option value="entities">Entities</option>
          </select>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 20px' }}>
          {visibleGroups.length === 0 ? (
            <p
              style={{
                margin: '16px 12px',
                fontSize: type.size.body,
                color: palette.textMuted,
              }}
            >
              No comments match these filters.
            </p>
          ) : (
            visibleGroups.map(({ group, rows }) => {
              const { number, label } = resolveLabel(group)
              return (
                <div key={group.key} style={{ marginTop: 16 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 8,
                      padding: '0 8px',
                      marginBottom: 6,
                    }}
                  >
                    {number && (
                      <span
                        style={{
                          fontFamily: type.family.mono,
                          fontSize: type.size.eyebrow,
                          color: palette.textFaint,
                          letterSpacing: '0.08em',
                        }}
                      >
                        {number}
                      </span>
                    )}
                    <span
                      style={{
                        flex: 1,
                        fontSize: type.size.body,
                        fontWeight: type.weight.semibold,
                        color: palette.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={label}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        fontFamily: type.family.mono,
                        fontSize: type.size.eyebrow,
                        color: palette.textMuted,
                      }}
                    >
                      ({group.unresolved}/{group.total})
                    </span>
                  </div>
                  {rows.map(c => (
                    <CommentRow
                      key={c.id}
                      palette={palette}
                      comment={c}
                      onClick={() =>
                        onSelectComment({ type: group.anchorType, id: group.anchorID, label }, c.id)
                      }
                    />
                  ))}
                </div>
              )
            })
          )}
        </div>
      </div>
    </Drawer>
  )
}

const liveEntityLabel = (type: AnchorGroup['anchorType'], id: string): string | undefined => {
  if (typeof document === 'undefined') return undefined
  const el = document.querySelector(
    `[data-comment-anchor-type="${type}"][data-comment-anchor-id="${id}"]`
  )
  return el?.getAttribute('data-comment-label') ?? undefined
}

const CommentRow = ({
  palette,
  comment,
  onClick,
}: {
  palette: SurfacePalette
  comment: Comment
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '10px 12px',
      marginBottom: 6,
      borderRadius: radius.md,
      border: `1px solid ${palette.hairline}`,
      background: palette.bg,
      color: palette.text,
      cursor: 'pointer',
      fontFamily: type.family.sans,
    }}
  >
    <div
      style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}
    >
      <span
        style={{
          fontSize: type.size.body,
          fontWeight: type.weight.semibold,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {authorName(comment)}
      </span>
      <span style={{ fontSize: type.size.eyebrow, color: palette.textFaint, whiteSpace: 'nowrap' }}>
        {formatRelative(comment.createdAt)}
      </span>
    </div>
    <div
      style={{
        marginTop: 4,
        fontSize: type.size.body,
        color: palette.textMuted,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
      }}
    >
      {comment.body}
    </div>
    {comment.resolved && (
      <span
        style={{
          display: 'inline-block',
          marginTop: 6,
          padding: '1px 8px',
          borderRadius: 999,
          border: `1px solid ${palette.hairline}`,
          fontFamily: type.family.mono,
          fontSize: 10,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: palette.textFaint,
        }}
      >
        Resolved
      </span>
    )}
  </button>
)
